/**
 * CSV export for the Space Yield Report.
 * Exports only visible columns, respecting user's column picker selection.
 */

import type { ProcessedUnit } from './types'
import { formatUnitType, formatStatus, formatSegment } from './compute'

interface CsvColumn {
  id: string
  header: string
  value: (u: ProcessedUnit) => string
}

const ALL_CSV_COLUMNS: CsvColumn[] = [
  // Core
  { id: 'unit', header: 'Unit', value: (u) => u.unit_code },
  { id: 'status', header: 'Status', value: (u) => formatStatus(u.status) },
  { id: 'yield', header: 'Revenue/Sqft', value: (u) => u.yield_per_sqft.toFixed(2) },
  // Default
  { id: 'sqft', header: 'Sqft', value: (u) => u.sqft.toString() },
  { id: 'tenant', header: 'Tenant', value: (u) => u.tenant_name || '' },
  { id: 'lease_rate', header: 'Lease Rate ($/mo)', value: (u) => u.lease_rate?.toFixed(2) || '' },
  { id: 'trans_rev', header: 'Transient Revenue ($/mo)', value: (u) => u.transient_revenue?.toFixed(2) || '' },
  { id: 'contract_end', header: 'Contract End', value: (u) => u.lease_end || '' },
  // Extended
  { id: 'annual_value', header: 'Annual Value', value: (u) => u.annual_value?.toFixed(0) || '' },
  { id: 'rate_gap', header: 'Rate Gap %', value: (u) => u.rate_gap_pct?.toFixed(0) || '' },
  { id: 'vacancy_cost', header: 'Vacancy Cost', value: (u) => u.vacancy_cost?.toFixed(0) || '' },
  { id: 'trans_rate_night', header: 'Trans. Avg Rate/Night', value: (u) => u.trans_rate_per_night?.toFixed(0) || '' },
  { id: 'utilization', header: 'Utilization %', value: (u) => u.utilization_pct?.toString() || '' },
  { id: 'renewal', header: 'Renewal Status', value: (u) => u.renewal_status || '' },
  { id: 'days_to_expiry', header: 'Days to Expiry', value: (u) => u.days_to_expiry?.toString() || '' },
  { id: 'escalation', header: 'Escalation %', value: (u) => {
    if (u.escalation_pct == null || u.escalation_type === 'none') return ''
    const type = u.escalation_type === 'cpi' ? 'CPI' : 'Fixed'
    return `${u.escalation_pct}% (${type})`
  }},
  // Fuel columns
  { id: 'fuel_rev', header: 'Fuel Revenue ($/mo)', value: (u) => u.fuel_revenue?.toFixed(2) || '' },
  { id: 'fuel_commitment', header: 'Fuel Commitment', value: (u) => {
    if (!u.fuel_commitment_gallons) return ''
    return `${u.fuel_delivered_gallons ?? 0}/${u.fuel_commitment_gallons} (${u.fuel_commitment_pct ?? 0}%)`
  }},
  { id: 'total_value', header: 'Total Tenant Value ($/mo)', value: (u) => u.total_tenant_value?.toFixed(2) || '' },
  { id: 'total_value_per_sqft', header: 'Total Value/Sqft', value: (u) => u.total_value_per_sqft?.toFixed(2) || '' },
  // Always-included context columns (not in column picker but useful for CSV)
  { id: '_building', header: 'Building', value: (u) => u.area_name },
  { id: '_type', header: 'Type', value: (u) => formatUnitType(u.unit_type) },
  { id: '_segment', header: 'Revenue Segment', value: (u) => formatSegment(u.revenue_segment) },
  { id: '_contract', header: 'Contract #', value: (u) => u.contract_number || '' },
  { id: '_lease_type', header: 'Lease Type', value: (u) => u.lease_type || '' },
  { id: '_underpriced', header: 'Underpriced', value: (u) => u.is_underpriced ? 'Yes' : '' },
]

export function exportCsv(units: ProcessedUnit[], period: string, visibleColumns?: string[]): void {
  // Determine which columns to export
  const colSet = visibleColumns ? new Set(visibleColumns) : null

  const exportColumns = ALL_CSV_COLUMNS.filter((col) => {
    // Always include context columns (prefixed with _)
    if (col.id.startsWith('_')) return true
    // If no column set specified, include all
    if (!colSet) return true
    // Otherwise respect visibility
    return colSet.has(col.id)
  })

  const headers = exportColumns.map((c) => c.header)
  const rows = units.map((u) => exportColumns.map((c) => c.value(u)))

  const csvContent = [headers, ...rows]
    .map((row) => row.map(escapeCsvField).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `space-yield-${period}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function escapeCsvField(field: string): string {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`
  }
  return field
}
